import React, { createContext, useContext, useEffect, useState } from 'react';

import Request from 'files/Request.js';

/* Buying a Deep Look pass, and the three other things this button has to be.
 *
 * Built on the same principle as QuickLookCta: the ask depends on where the
 * reader actually stands, and pitching somebody the thing they already own
 * reads worse than saying nothing. The difference is where the state lives. A
 * Quick Look is in this browser's storage, so the component can read it. A pass
 * is a row on the server tied to an account, so it has to be asked for.
 *
 * Four positions:
 *
 *   - signed out          buy it; sign in happens on the way
 *   - signed in, no pass  buy it, straight to Stripe
 *   - pass still running   they own this; tell them how to connect it
 *   - just came back       confirm what happened and carry on from there
 *
 * The third one is the reason this is not a link. Somebody with thirty days
 * left who lands on the sales page has usually come back because they could not
 * remember how to connect it, and the most useful button on the page for them
 * is not one that charges them again.
 */

/* One status lookup for the page rather than one per button.
 *
 * The page renders this button twice, in the header and at the foot, and both
 * need the same answer. Two components each asking on mount is two requests for
 * one fact, and worse, two answers that can disagree while one is still in
 * flight — which shows up as a header offering to sell a pass to somebody the
 * footer has already recognised. */
const StatusContext = createContext(null);

const request = async (utils, type) => {
    return await Request.post(utils, '/deep_look/', { type: type });
}

export const DeepLookStatus = ({ children, utils }) => {

    /* null while it is still being asked. The distinction matters: "we do not
       know yet" and "signed out" look the same in a boolean and must not draw
       the same button, because drawing the buy button and then swapping it for
       "you already have this" is the page changing its mind in front of
       somebody. */
    const [status, setStatus] = useState(null);

    useEffect(() => {

        let cancelled = false;

        const ask = async () => {
            try {
                const result = await request(utils, 'status');
                if(!cancelled) {
                    setStatus(result);
                }
            } catch(e) {
                /* A status lookup that fails is not worth telling anybody
                   about. The page still sells, the button still works, and the
                   worst case is that somebody who already has a pass gets shown
                   the buy button. Checkout would then recognise them anyway. */
                if(!cancelled) {
                    setStatus({ signed_in: false, pass: false });
                }
            }
        }
        ask();

        return () => { cancelled = true };
    }, [utils]);

    return (
        <StatusContext.Provider value={status}>{children}</StatusContext.Provider>
    )
}

export const useDeepLookStatus = () => useContext(StatusContext);

/* The button itself, which is the same object in the header and at the foot.
 *
 * Sign in and checkout are one press, not two. The signed-out path asks the
 * server where AuthKit lives and moves the browser there, and AuthKit returns
 * to /deep-look/signed-in, which is a server route that finishes the sign in
 * and sends them back here. That is one redirect more than a signed-in buyer
 * takes, and it is invisible to the person pressing the button. Making them
 * press "sign in", land back on the page, and then find the buy button again is
 * the version that loses people. */
export const DeepLookButton = ({ className = 'dl-btn', label, utils }) => {

    const status = useDeepLookStatus();
    const [working, setWorking] = useState(false);
    const [failed, setFailed] = useState(null);

    const onPress = async evt => {

        evt.preventDefault();
        if(working) {
            return;
        }
        setWorking(true);
        setFailed(null);

        try {
            /* Signed in goes to Stripe. Signed out goes to AuthKit and comes
               back here, where the button will be in the signed-in state. */
            const type = status && status.signed_in ? 'checkout' : 'sign_in';
            const result = await request(utils, type);

            if(!result || !result.url) {
                throw new Error('That did not come back with anywhere to go.');
            }
            /* assign rather than replace: somebody who backs out of a card form
               should land on the page they were reading, not two pages before
               it */
            window.location.assign(result.url);

        } catch(e) {
            setWorking(false);
            setFailed(e.message || 'Something went wrong starting that.');
        }
    }

    /* Nothing at all until the status lands, rather than a button that changes
       its mind. The header reserves the space either way, so this does not move
       the page when it arrives. */
    if(!status) {
        return <span aria-hidden={'true'} className={'dl-cta-waiting'} />;
    }

    if(status.pass) {
        return (
            <a className={className} href={'/deep-look/ready'}>
                Connect it to your agent <span className={'dl-arrow'}>→</span>
            </a>
        )
    }

    return (
        <>
            <button className={className} disabled={working} onClick={onPress} type={'button'}>
                {working ? 'One moment' : label} <span className={'dl-arrow'}>→</span>
            </button>
            {failed && <p className={'dl-cta-error'} role={'alert'}>{failed}</p>}
        </>
    )
}

/* The closing block, which is the callout the markdown file ends on plus the
   button. Same shape as QuickLookCta so the two pages end the same way. */
const DeepLookCta = ({ callout, utils }) => {

    const status = useDeepLookStatus();

    if(status && status.pass) {
        return (
            <aside className={'ar-callout is-quiet'}>
                <p>
                    Your pass is still running, so there is nothing to buy here. If you came
                    back to work out how to connect it again, that is on the next page.
                </p>
                <a className={'dl-quiet'} href={'/deep-look/ready'}>
                    Connect it to your agent <span className={'dl-arrow'}>→</span>
                </a>
            </aside>
        )
    }

    return (
        <aside className={'ar-callout'}>
            <p>{callout}</p>
            <DeepLookButton label={'Start a Deep Look'} utils={utils} />
        </aside>
    )
}

export default DeepLookCta;
